// Claude: BuyerSubscriptions.tsx — Admin page for managing buyer subscription plans 11-Apr-2026
// Claude: pricePerRFX → subscriptionFee 25-Apr-2026
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Modal, Table } from "antd";
import BuyerSubscriptionPlanModal from "./BuyerModals/BuyerSubscriptionPlanModal";
import React, { useEffect, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BuyerSubscriptionPlan } from "../../Interface/AdminInterfaces";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { AdminAPI } from "../../Apis/adminAPI";
import { AdminBuyerAPI } from "../../Apis/adminBuyerAPI";
import Loader from "../../Components/Loader/Loader";
import { errorHandler } from "../../Utils/handler";
import { useDispatch, useSelector } from "react-redux";
import {
  resetBuyerForm,
  setBuyerIsActive,
  setBuyerPendingMeetingsLimit,
  setBuyerPlanName,
  setBuyerRfxLimit,
  setBuyerMinAssuredResponses,
  setSubscriptionFee,
  setBuyerCurrency,
  setBuyerSubscriptions,
} from "../../redux/slices/buyerSubscriptionSlice"; // Claude: closedMeetingsLimit removed 23-04-2026

const BuyerSubscriptions = () => {
  const { subscriptions, ...planDetails } = useSelector((state: any) => state.buyerSubscription);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [deleteid, setDeleteId] = useState("");
  const [usageCount, setUsageCount] = useState<number | null>(null);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);

  const { error, success } = useToastWrapper();

  const showModal = () => setIsModalVisible(true);
  const handleModalClose = () => {
    setIsModalVisible(false);
    setViewOnly(false);
    dispatch(resetBuyerForm());
  };

  const getData = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getBuyerSubscriptions();
      dispatch(setBuyerSubscriptions(res.data.data.subscriptions));
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  // Claude: fetch usage count — block edit/delete if buyers associated, else proceed directly 26-04-2026
  const fetchUsageAndPrompt = async (planId: string, action: 'edit' | 'delete') => {
    setLoading(true);
    try {
      const res = await AdminBuyerAPI.getBuyerSubscriptionUsage(planId);
      const count = res.data.count;
      setDeleteId(planId);
      if (count > 0) {
        setUsageCount(count);
        setIsUsageModalOpen(true);
      } else {
        if (action === 'edit') {
          setEdit(true);
          setActivePlanEdit(planId);
          showModal();
        } else {
          handleDelete(planId);
        }
      }
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const handleViewPlan = () => {
    setIsUsageModalOpen(false);
    setUsageCount(null);
    setViewOnly(true);
    setActivePlanEdit(deleteid);
    showModal();
  };

  const setActivePlanEdit = (planId: string) => {
    const plan = subscriptions.find((item: any) => item._id === planId);
    if (plan) {
      setEditId(planId);
      dispatch(setBuyerPlanName(plan.planName));
      dispatch(setBuyerRfxLimit(String(plan.rfxLimit ?? 0)));
      dispatch(setBuyerPendingMeetingsLimit(String(plan.pendingMeetingsLimit ?? 0)));
      dispatch(setBuyerMinAssuredResponses(String(plan.minAssuredResponses ?? 0)));
      dispatch(setSubscriptionFee(String(plan.subscriptionFee ?? 0)));
      dispatch(setBuyerCurrency(plan.currency ?? 'USD'));
      dispatch(setBuyerIsActive(plan.isActive));
    }
  };

  const handleDelete = async (key: any) => {
    setLoading(true);
    try {
      await AdminAPI.deleteBuyerSubscription(key);
      success("Plan deleted successfully", "");
      getData();
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!planDetails.planName) {
      error("Please enter a plan name", "");
      return false;
    }
    return true;
  };

  // Claude: parse all numeric limit fields before sending to backend 21-04-2026
  // Claude: exclude rfpResponsePendingLimit — system constant, not stored per plan 25-Apr-2026
  const buildBuyerPayload = () => {
    const { rfpResponsePendingLimit, ...rest } = planDetails;
    return {
      ...rest,
      rfxLimit: parseInt(planDetails.rfxLimit),
      pendingMeetingsLimit: parseInt(planDetails.pendingMeetingsLimit),
      minAssuredResponses: parseInt(planDetails.minAssuredResponses) || 0,
      subscriptionFee: parseFloat(planDetails.subscriptionFee) || 0,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    if (edit) {
      try {
        await AdminAPI.updateBuyerSubscriptionPlan(editId, buildBuyerPayload());
        dispatch(resetBuyerForm());
        handleModalClose();
        getData();
        success("Plan updated successfully", "");
        setEdit(false);
        setEditId("");
      } catch (err) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    } else {
      try {
        await AdminAPI.addBuyerSubscriptionPlan(buildBuyerPayload());
        dispatch(resetBuyerForm());
        handleModalClose();
        getData();
        success("Plan added successfully", "");
      } catch (err) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    }
  };

  // Claude: updated columns — Plan Name, Status, Subscription Fee, Currency, RFX Publish Limit, MAR 25-Apr-2026
  const columns = [
    { title: "Plan Name", dataIndex: "planName", key: "planName" },
    {
      title: "Status", dataIndex: "isActive", key: "isActive",
      render: (val: boolean) => (val ? "Active" : "Inactive"),
    },
    {
      title: "Subscription Fee", dataIndex: "subscriptionFee", key: "subscriptionFee",
      render: (val: any) => val ?? 0,
    },
    { title: "Currency", dataIndex: "currency", key: "currency" },
    {
      title: "RFX Publish Limit", dataIndex: "rfxLimit", key: "rfxLimit",
      render: (val: any) => val ?? 0,
    },
    {
      title: "MAR", dataIndex: "minAssuredResponses", key: "minAssuredResponses",
      render: (val: any) => val ?? 0,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, object: any) => (
        <Flex gap={1}>
          <Button
            h={"100%"} background={"primary.main"} color={"white"} px={3} py={1} fontSize={"xs"}
            _hover={{ background: "#2E7D32", color: "white" }}
            onClick={() => fetchUsageAndPrompt(object._id, 'edit')}
          >
            Edit
          </Button>
          <Button
            h={"100%"} background={"secondary.light"} color={"secondary.main"} px={3} py={1} fontSize={"xs"}
            _hover={{ background: "#C62828", color: "white" }}
            onClick={() => fetchUsageAndPrompt(object._id, 'delete')}
          >
            Delete
          </Button>
        </Flex>
      ),
    },
  ];

  useEffect(() => { getData(); }, []);

  return (
    <div className="p-4">
      {loading ? <Loader /> : null}

      {/* Claude: plan-in-use blocking modal — view only when buyers are associated 26-04-2026 */}
      <Modal
        title="Plan In Use"
        footer={null}
        onCancel={() => { setIsUsageModalOpen(false); setUsageCount(null); }}
        open={isUsageModalOpen}
      >
        <Text my={5} mt={8}>
          {usageCount === 1
            ? `1 buyer is associated with this plan.`
            : `${usageCount ?? 0} buyers are associated with this plan.`}{" "}
          You can only view the plan.
        </Text>
        <Flex justifyContent={"space-between"}>
          <Button
            h={"100%"} background={"gray.200"} color={"gray.700"} px={3} py={1}
            _hover={{ background: "gray.300" }}
            onClick={() => { setIsUsageModalOpen(false); setUsageCount(null); }}
          >
            Cancel
          </Button>
          <Button
            h={"100%"} background={"primary.main"} color={"white"} px={3} py={1}
            _hover={{ background: "#2E7D32", color: "white" }}
            onClick={handleViewPlan}
          >
            View
          </Button>
        </Flex>
      </Modal>

      {/* Add/Edit modal */}
      <BuyerSubscriptionPlanModal
        isOpen={isModalVisible}
        onClose={handleModalClose}
        edit={edit}
        onSubmit={handleSubmit}
        loading={loading}
        viewOnly={viewOnly}
      />

      <Flex alignItems="center" gap={2} mt={"10px"}>
        <Text size={"lg"} fontWeight={600}>Buyer Subscription Plans</Text>
        <Button
          size="sm" fontSize="xs" h={"28px"} background={"#0E122F"} color={"white"} px={3} gap={2}
          _hover={{ background: "#2E7D32" }}
          onClick={() => { setEdit(false); setEditId(""); dispatch(resetBuyerForm()); showModal(); }}
        >
          <AiOutlinePlus /> Add Plan
        </Button>
      </Flex>

      <Box mt={5} sx={{ "& .ant-table": { fontSize: "12px" }, "& .ant-table-cell": { fontSize: "12px" } }}>
        <Table
          size="small"
          style={{ width: "100%" }}
          tableLayout="fixed"
          dataSource={subscriptions}
          bordered
          columns={columns}
          pagination={false}
          rowKey="_id"
        />
      </Box>
    </div>
  );
};

export default BuyerSubscriptions;
